
import { defineComponent } from 'vue';
import CloseBtn from "../pageElements/CloseBtn.vue";
import TileDisplay from "../main/TileDisplay.vue";

export default defineComponent({
    name: "ProjectModal",

    components:{
        CloseBtn,
        TileDisplay
    },

    props:[
        "data"
    ],

    emits:[
        "hideModals"
    ],

    data(){
        return{

                /* Modal */

            // Elements
            innerModal: null as null | HTMLElement,
            currTile: null as null | HTMLElement,
            tiles: null as null | HTMLElement[],
            
            // Scroll
            scrollOffset: window.innerHeight / 2, // Show the btn if scrolled this amount
            showModalScrollbtn: false,
            resizeTimer: 0,

                /* BG Color */

            // Settings
            imgScaledWidth: 100,

            // Elements
            canvas: null as HTMLCanvasElement | null,
            ctx: null as CanvasRenderingContext2D | null,
            placeholderImg:require("@/assets/img/0-portfolio/placeholder-img.svg"),

                /* Other */

            containsObjects: false,
            
        } 
    },

    mounted(){

            /* Elements */

        // Inner modal
        this.innerModal = this.$refs.innerModal as HTMLElement;

        // Modal project image canvas
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });

        // Get the tiles
        this.tiles = this.$refs.designTile as HTMLElement[];

            /* Images */

        // Load the project design images &&
        // Set the modal auto background color
        const images = this.$refs.designProjImg;
        
        this.$nextTick(() => {
            this.imgLazyLoading(images as HTMLImageElement[]);
        });

            /* Modals */

        // Modal placement on resize
        window.addEventListener("resize", this.adjustModalPlacement);

        // Show the modal scroll btn (when scrolled down)
        this.innerModal.addEventListener("scroll", this.modalScrollBtnVisibility);

            /* Website & App Designs */

        if (typeof this.data.designs[0] === 'object' &&
            this.data.designs[0] !== null){

            this.containsObjects = true;
            
        }

    },

    methods:{

            /* Scroll & Resize */

        closeModalScroll(tile?: HTMLElement){

            const designImgOuter = tile!.querySelector(".design-proj-img-outer");

            designImgOuter?.scrollTo({
                top:0,
                behavior:'smooth'
            });
            
        },

        modalScrollBtnVisibility(){

            if (this.innerModal!.scrollTop > this.scrollOffset){
                this.showModalScrollbtn = true;
            } else {
                this.showModalScrollbtn = false;
            }

        },

        scrollTopModal(){
            this.innerModal!.scrollTo({
                top:0
            });
        },

        modalToTop(){
            this.innerModal!.scrollTo({
                top:0,
                behavior:"smooth"
            });
        },

        adjustModalPlacement(){

            // Set a new scroll offset
            this.scrollOffset = window.innerHeight / 2;

            clearTimeout(this.resizeTimer);

            if (this.currTile &&
            this.currTile.classList.contains("design-open")){

                this.currTilePos(this.currTile);

                // Ensure the correct placement
                // Sometimes the resize event dwould not be registered correctly
                this.resizeTimer = setTimeout(() => {
                    this.currTilePos(this.currTile!);
                }, 420);

            }

        },

            /* Modal Behavior */

        hideDesignModals(currTile?: HTMLElement){

            this.tiles?.forEach((tile) => {

                this.closeModalScroll(tile);

                (tile.querySelector(".design-proj-inner") as HTMLElement)!.style.transform = "translate3d(0px, 0px, 0px)";
                    
                tile.classList.remove("design-open", "no-trans");

                if (tile !== currTile){
                    setTimeout(() => {
                        tile.classList.remove("design-opacity");
                    }, 600);
                }

            });

        },

        hideModals(currTile: HTMLElement){

            // Disable the modal mode
            document.documentElement.classList.remove("modal-mode");

            // Hide the tile modals
            this.hideDesignModals(currTile);

        },

        currTilePos(tile: HTMLElement){

            // Get the tile position
            const rect = tile.getBoundingClientRect();
            const transX = -rect.left;
            const transY = -rect.top;

            // Set the transform property (position modal in the center)
            (tile.querySelector(".design-proj-inner") as HTMLElement)!.style.transform = 
                `translate3d(${transX - 1}px, ${transY}px, 0px)`;

        },

        tileModal(e: Event){

            const tile = (e.target as HTMLElement).closest('.design-project') as HTMLElement;

            // Hide the other modals
            this.hideModals(tile);

            // Set the page to the modal mode
            document.documentElement.classList.add("modal-mode");

            // Set the current modal tile
            this.currTile = tile;

            // Adjust the modal position
            this.currTilePos(tile);

            // Show modal
            tile.classList.add("design-open", "design-opacity");

            // Remove the transition effect after the modal has covered the page
            setTimeout(() => {
                tile.classList.add("no-trans");
            }, 750);

        },

            /* Modal Background Color & Images */

        autoBgMode(tileImg: HTMLImageElement){

            const tile = tileImg.closest(".design-project") as HTMLImageElement;
            const canvas = this.canvas;
            const ctx = this.ctx;

            // Create a new image element
            const img:HTMLImageElement = new Image();
            img.src = tileImg.src;

            img.addEventListener('load', () => {
                if (canvas && ctx){

                    // Image ratio
                    const imgRatio = img.width / img.height;

                    // Image new height
                    const imgScaledHeight = 
                        Math.round(this.imgScaledWidth / imgRatio);
                    
                    // Create a canvas element
                    canvas.width = this.imgScaledWidth;
                    canvas.height = imgScaledHeight;

                    // Draw the image on the canvas
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    // Get the image data
                    const imageData:ImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data:Uint8ClampedArray = imageData.data;

                    // Max and min color values
                    let maxVal = 0, minVal = 0, totAlpha = 0;

                    for (let i = 0; i < data.length; i += 4){
                        if (data[i + 3] > 0){
                            const   r = data[i], 
                                    g = data[i + 1], 
                                    b = data[i + 2];

                            maxVal += Math.max(r, g, b);
                            minVal += Math.min(r, g, b);
                            
                            totAlpha++;
                        }
                    }

                    // Calculate lightness
                    const lightnessSum = (maxVal + minVal) / 2;
                    const lightness = Math.round(lightnessSum / totAlpha / 255 * 100);

                    // Change the bg color
                    if (lightness >= 65){
                        tile.classList.add("dark-modal");
                    } else {
                        tile.classList.remove("dark-modal");
                    }

                }
            });

        },

        imgLazyLoading(images: HTMLImageElement[]){

            images.forEach((image) => {

                const imgTile = image.closest(".design-project");

                if (imgTile){

                    const dataSrc = image.getAttribute('data-src') as string;

                    // Remove the placeholder animation
                    imgTile.classList.add("no-ph-anim");

                    // Replace the placeholder image with the actual image
                    image.src = dataSrc;
                   
                    // Auto background color adjustment
                    image.addEventListener('load', () => {
                        this.autoBgMode(image);
                    });

                }

            });

        },

        toggleModalBg(e: Event){
            (e.target as HTMLElement)!.closest(".design-project")!.classList.toggle("dark-modal");
        }

    }

});
